import { useState, useCallback } from "react";
import {
  startRegistration,
  startAuthentication,
} from "@simplewebauthn/browser";

import { axiosInstance, baseURL } from "../utils";
import axios from "axios";

// Helper to get CSRF token from cookies (set by Django)
const getCsrfToken = () => {
  const match = document.cookie.match(/csrftoken=([^;]+)/);
  return match ? match[1] : null;
};

const getPasskeyLoginErrorMessage = (err) => {
  // WebAuthn browser errors
  if (err.name === "NotAllowedError") {
    return "Passkey login was cancelled";
  }
  if (err.name === "NotFoundError") {
    return "No passkey found for this device";
  }

  const apiError = err.response?.data?.error;

  // No passkeys registered
  if (apiError === "Failed to generate login options") {
    return "No passkeys registered yet. Please sign in with your password.";
  }

  // Check for invalid/deleted passkey
  const errorArray = Array.isArray(apiError) ? apiError : [apiError];
  const hasIncorrectCode = errorArray.some(
    (e) => typeof e === "string" && e.includes("Incorrect"),
  );
  if (hasIncorrectCode) {
    return "This passkey is no longer valid. It may have been deleted. Please sign in with your password.";
  }

  // Join array errors or return string error
  if (Array.isArray(apiError)) {
    return apiError.join(". ");
  }
  if (apiError) {
    return apiError;
  }

  return err.message || "Failed to login with passkey";
};

export const usePasskeys = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Register a new passkey for the current user (must be logged in)
   * @param {string} name - Friendly name for the passkey (e.g., "MacBook Pro")
   * @param {string} password - User's password for reauthentication
   */
  const registerPasskey = useCallback(async (name, password) => {
    setLoading(true);
    setError(null);

    try {
      // Get registration options from server
      const { data } = await axiosInstance.get("/webauthn/register/options/");

      // Start WebAuthn registration ceremony in browser
      // Backend returns { options: { publicKey: { ... } } }
      const credential = await startRegistration({
        optionsJSON: data.options.publicKey,
      });

      // Send credential to server to complete registration (password required for reauth)
      await axiosInstance.post("/webauthn/register/", {
        credential,
        name,
        password,
      });

      return { success: true };
    } catch (err) {
      let errorMessage;
      if (err.name === "NotAllowedError") {
        errorMessage = "Passkey registration was cancelled";
      } else if (err.response?.status === 401) {
        errorMessage = "Incorrect password";
      } else {
        errorMessage =
          err.response?.data?.error ||
          err.message ||
          "Failed to register passkey";
      }
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get list of user's registered passkeys
   */
  const listPasskeys = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("/webauthn/passkeys/");
      return { success: true, passkeys: data.passkeys || [] };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.error || "Failed to fetch passkeys",
      };
    }
  }, []);

  /**
   * Rename a passkey
   * @param {string} id - Passkey ID
   * @param {string} name - New name
   */
  const renamePasskey = useCallback(async (id, name) => {
    try {
      await axiosInstance.patch(`/webauthn/passkeys/${id}/`, { name });
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.error || "Failed to rename passkey",
      };
    }
  }, []);

  /**
   * Delete a passkey
   * @param {string} id - Passkey ID
   */
  const deletePasskey = useCallback(async (id) => {
    try {
      await axiosInstance.delete(`/webauthn/passkeys/${id}/delete/`);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.error || "Failed to delete passkey",
      };
    }
  }, []);

  /**
   * Login with a passkey (no authentication required)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const loginWithPasskey = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get login options from server (public endpoint)
      const { data: optionsData } = await axios.get(
        `${baseURL}/webauthn/login/options/`,
        { withCredentials: true },
      );

      // Start WebAuthn authentication ceremony in browser
      const credential = await startAuthentication({
        optionsJSON: optionsData.options.publicKey,
      });

      // Send credential to server to complete login
      const csrfToken = getCsrfToken();
      const { data: loginData } = await axios.post(
        `${baseURL}/webauthn/login/`,
        { credential },
        {
          withCredentials: true,
          headers: csrfToken ? { "X-CSRFToken": csrfToken } : {},
        },
      );

      return { success: true, data: loginData };
    } catch (err) {
      const errorMessage = getPasskeyLoginErrorMessage(err);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check if WebAuthn/passkeys are supported in this browser
   */
  const isSupported = useCallback(() => {
    return (
      typeof window !== "undefined" && window.PublicKeyCredential !== undefined
    );
  }, []);

  return {
    registerPasskey,
    loginWithPasskey,
    listPasskeys,
    renamePasskey,
    deletePasskey,
    isSupported,
    loading,
    error,
    clearError: () => setError(null),
  };
};
